/**
 * EnemiesManager.js
 * Manages the state on in-game enemies
 */

import { Game, DesignDataTypes, GameTimer } from "pixi_core";

import { Common } from "../game/Common";
import { Enemy } from "../Enemies/Enemy";
import { ChitauriArtificier } from "../Enemies/ChitauriArtificier";
import { ChitauriFighter } from "../Enemies/ChitauriFighter";
import { ChitauriRider } from "../Enemies/ChitauriRider";
import { CorvusGlaive } from "../Enemies/CorvusGlaive";
import { CullObsidian } from "../Enemies/CullObsidian";
import { EbonyMaw } from "../Enemies/EbonyMaw";
import { HeavyUltronsSentries } from "../Enemies/HeavyUltronsSentries";
import { Leviathan } from "../Enemies/Leviathan";
import { Loki } from "../Enemies/Loki";
import { LokiCopy } from "../Enemies/LokiCopy";
import { ProximaMidnight } from "../Enemies/ProximaMidnight";
import { RedRoomGuardA } from "../Enemies/RedRoomGuardA";
import { RedRoomGuardB } from "../Enemies/RedRoomGuardB";
import { RedRoomGuardC } from "../Enemies/RedRoomGuardC";
import { SupportUltronsSentries } from "../Enemies/SupportUltronsSentries";
import { LokiWeakened } from "../Enemies/LokiWeakened";
import { Thanos } from "../Enemies/Thanos";
import { Ultron } from "../Enemies/Ultron";
import { UltronsSentries } from "../Enemies/UltronsSentries";

import {
    Enemies,
    BubbleColor,
    BUBBLE_COLOR_NAME_TO_ID,
    CheatEvents,
    GameEvents,
    shuffle,
    NB_MAX_ENEMIES,
    BUBBLE_COLOR_ID_TO_NAME,
    BuffDebuff
} from "../Constants";

export class EnemiesManager {
    /**
     * @public
     */
    constructor() {
        Common.enemies = this;

        /** @private @type {Enemy[]} */
        this._currentEnemies = [null, null, null];

        /** @private @type {Number} */
        this._nbEnemies = 0;

        /** @protected @type {Boolean} */
        this._cheatEnemyAlwaysAttack = false;

        /** @private @type {Number} */
        this._cheatDamageMultiplier = 1;

        /** @private @type {Number} */
        this._waspDamageDuration = 0;

        /** @private @type {Number} */
        this._waspDamageMultiplier = 1;

        /** @private @type {Number} */
        this._allOutDamageDuration = 0;

        /** @private @type {Number} */
        this._allOutDamageMultiplier = 1;

        /** @private @type {Boolean} */
        this._heroesDefeated = false;

        /** @private @type {Number[]} */
        this._bubblesPopped = [0, 0, 0, 0, 0, 0, 0, 0];

        /** @private @type {Enemy[]} - List of enemies performing an attack in the current attack phase */
        this._attackers = [];

        /** @private @type {Boolean} */
        this._preemptiveStrike = false;

        /** @private @type {Boolean[]} */
        this._currentEnemiesWeaknesses = [];

        /** @private @type {String[]} */
        this._availableColor = [];

        /** @private @type {GameTimer} */
        this._timerDelayBetweenAttacks = null;

        Game.on(GameEvents.eGAME_ENEMY_DEAD, this._onEnemyDead, this);
        Game.on(GameEvents.eBUBBLE_CLEARED, this._onBubblesCleared, this);
        Game.on(GameEvents.eATTACKPHASE_ENEMY_ATTACK_COMPLETED, this._onEnemyAttackCompleted, this);

        Game.on(GameEvents.eREQUEST_ACTIVE_ENEMIES, this._onRequestActiveEnemies, this);
        Game.on(GameEvents.eGAME_ABILITY_DUPLICATE, this._onAbilityAddEnemy, this);
        Game.on(GameEvents.eGAME_ABILITY_TROOPS_POD, this._onAbilityAddEnemy, this);
        Game.on(GameEvents.eGAME_ABILITY_MASS_PRODUCTION, this._onAbilityAddEnemy, this);
        Game.on(GameEvents.eGAME_ABILITY_UPGRADE_HEAL, this._onUltronUpgradeHeal, this);
        Game.on(GameEvents.eGAME_ABILITY_UPGRADE_WEAKNESSES, this._onUltronUpgradeWeaknesses, this);
        Game.on(GameEvents.eGAME_ABILITY_REALITY_SHIFT, this._onThanosRealityShift, this);
        Game.on(GameEvents.eGAME_ABILITY_REPAIR, this._onEnemyRepair, this);
        Game.on(GameEvents.eGAME_ABILITY_SHIELD_ENEMY, this._onEnemyShield, this);
        Game.on(GameEvents.eGAME_ABILITY_REGENERATION, this._onEnemyRegeneration, this);

        Game.on(GameEvents.eHERO_ABILITY_WASP, this._waspAbilityDamageMultiplier, this);
        Game.on(GameEvents.eHEROES_ABILITY_DURATION_TICK, this._tickDownAbilityDurations, this);
        Game.on(GameEvents.eHERO_ABILITY_BLACK_WIDOW, this._blackWidowAilityIcreaseAttackTimer, this);
        Game.on(GameEvents.eHEROES_ALL_OUT_ATTACK_CUTIN_DONE, this._allOutAttackDamageMultiplier, this);
        Game.on(GameEvents.eHEROES_DEAD, this._onHeroesDead, this);

        Game.on(CheatEvents.eENEMY_ALWAYS_ATTACK, this._onCheatsEnemyAlwaysAttack, this);
        Game.on(CheatEvents.eDAMAGE_MULTIPLIER, this._onCheatsDamageMultiplier, this);
        Game.on(CheatEvents.eDEPLETE_ENEMIES, this._onCheatDepleteEnemies, this);
    }

    /**
     * @public
     * @param {import("../Constants").GenerationConstraints} constraints
     */
    setConstraints(constraints) {
        this._availableColor = [];
        let generationConstraints = JSON.parse(JSON.stringify(constraints));

        if (generationConstraints.RedPercent !== 0) this._availableColor.push(BUBBLE_COLOR_ID_TO_NAME[BubbleColor.eRED]);
        if (generationConstraints.OrangePercent !== 0) this._availableColor.push(BUBBLE_COLOR_ID_TO_NAME[BubbleColor.eORANGE]);
        if (generationConstraints.YellowPercent !== 0) this._availableColor.push(BUBBLE_COLOR_ID_TO_NAME[BubbleColor.eYELLOW]);
        if (generationConstraints.GreenPercent !== 0) this._availableColor.push(BUBBLE_COLOR_ID_TO_NAME[BubbleColor.eGREEN]);
        if (generationConstraints.BluePercent !== 0) this._availableColor.push(BUBBLE_COLOR_ID_TO_NAME[BubbleColor.eBLUE]);
        if (generationConstraints.PurplePercent !== 0) this._availableColor.push(BUBBLE_COLOR_ID_TO_NAME[BubbleColor.ePURPLE]);

        this._availableColor = shuffle(this._availableColor);
    }

    /**
     * @public
     */
    beginLevel() {
        // Reset heroes abilities
        this._waspDamageDuration = 0;
        this._waspDamageMultiplier = 1;
        this._allOutDamageDuration = 0;
        this._allOutDamageMultiplier = 1;
        this._heroesDefeated = false;
    }

    /**
     * @public
     * @param {Number} deltaTime
     */
    updateLoop(deltaTime) {
        for (let i = 0; i < this._currentEnemies.length; ++i) {
            const enemy = this._currentEnemies[i];
            if (enemy) {
                enemy.updateLoop(deltaTime);
            }
        }
    }

    /**
     * @public
     */
    endLastWave() {
        for (let enemy of this._currentEnemies) {
            if (enemy) {
                enemy.destroy();
                enemy = null;
            }
        }
        this._currentEnemies = [null, null, null];
    }

    /**
     * @public
     * @param {Number} currentWave
     * @param {Object[]} levelWaves
     */
    startNewWave(currentWave, levelWaves) {
        if (this._timerDelayBetweenAttacks) {
            this._timerDelayBetweenAttacks.destroy();
            this._timerDelayBetweenAttacks = null;
        }

        this._nbEnemies = 0;

        if (currentWave < levelWaves.length) {
            const enemies = levelWaves[currentWave].filter((element) => element !== Enemies.eNONE);
            const nbEnemy = enemies.length;
            for (let i = 0; i < nbEnemy; ++i) {
                let index;
                if (nbEnemy === 1) {
                    index = 1;
                } else if (nbEnemy === 2) {
                    index = i === 0 ? 0 : 2;
                } else {
                    index = i;
                }

                let enemy = this._createEnemy(enemies[i], index, false);

                this._currentEnemies[index] = enemy;

                if (enemy !== null) {
                    enemy.removeShield();

                    if (this._cheatEnemyAlwaysAttack) {
                        enemy.cheatsEnemyAlwaysAttack(true);
                    }

                    if (enemy.isABoss) {
                        Game.fire(GameEvents.eBOSS_SET_CUTIN, enemy);
                    }

                    ++this._nbEnemies;
                }
            }

            Game.fire(GameEvents.eGAME_HIDE_ENEMIES);
            Game.fire(GameEvents.eGAME_SET_NUMBER_OF_ENEMIES, this._nbEnemies, false);

            Game.audio.playSfx("IDSND_ENEMY_APPEARS");
            for (const enemy of this._currentEnemies) {
                if (enemy) {
                    Game.fire(GameEvents.eGAME_SET_ENEMY, enemy.index, enemy, enemy.weakness, currentWave === 0);
                    Game.fire(GameEvents.eGAME_SET_ENEMY_ATTACK_TIMER, enemy.index, enemy.currentAttackTimer, false);
                }
            }

            this._recalculateEnemyWeaknesses();
        }
    }

    /**
     * The enemies have won.  Oh the tragedy!
     * @private
     */
    _onHeroesDead() {
        this._heroesDefeated = true;
    }

    /**
     * The player did not clear any bubbles with this shot
     * @public
     */
    onMatchMiss() {
        for (let i = 0; i < this._currentEnemies.length; ++i) {
            const enemy = this._currentEnemies[i];
            if (enemy) {
                if (enemy.blackWidowStunTimer <= 0) {
                    enemy.lowerAttackTimer();
                    Game.fire(GameEvents.eGAME_SET_ENEMY_ATTACK_TIMER, i, enemy.currentAttackTimer, true);
                }
            }
        }
    }

    /**
     * @private
     * @param {Number} timerIncrease
     */
    _blackWidowAilityIcreaseAttackTimer(timerIncrease) {
        for (let i = 0; i < this._currentEnemies.length; ++i) {
            const enemy = this._currentEnemies[i];
            if (enemy) {
                enemy.setBlackWidowStun(timerIncrease);
            }
        }
    }

    /**
     * Are there still enemies alive?
     * @public
     * @returns {Boolean}
     */
    areStillAlive() {
        let stillAlive = false;
        for (const enemy of this._currentEnemies) {
            if (enemy && enemy.isAlive) {
                stillAlive = true;
            }
        }

        return stillAlive;
    }

    /**
     * Are there enemies that will do a preemptive strike?
     * @public
     * @returns {Boolean}
     */
    haveFirstStrike() {
        let firstStrike = false;
        for (const enemy of this._currentEnemies) {
            if (enemy && enemy.firstStrike) {
                firstStrike = true;
            }
        }

        return firstStrike;
    }

    /**
     * The attack phase begins
     * @public
     * @param {Boolean} preemptiveStrike
     */
    attack(preemptiveStrike) {
        this._preemptiveStrike = preemptiveStrike;

        if (this._preemptiveStrike) {
            Game.fire(GameEvents.eGAME_BUBBLE_DELAY_COUNTDOWN);
        }

        this._attackers = [];

        for (const enemy of this._currentEnemies) {
            if (enemy && enemy.isAlive) {
                if (this._preemptiveStrike) {
                    if (enemy.firstStrike) {
                        this._attackers.push(enemy);
                    }
                } else {
                    this._attackers.push(enemy);
                }
            }
        }

        this._beginNextAttack();
    }

    /**
     * Begins the attack of the next enemy
     * @public
     */
    _beginNextAttack() {
        if (!this._heroesDefeated && this._attackers.length > 0) {
            const attacker = this._attackers.shift();
            attacker.attack(this._preemptiveStrike);
        } else {
            Game.fire(GameEvents.eENEMIES_ATTACK_PHASE);
        }
    }

    /**
     * @private
     * @param {Enemy} attacker
     */
    _onEnemyAttackCompleted(attacker) {
        this._timerDelayBetweenAttacks = Game.windows.getCurrentWindow().createTimer(Common.settings.attackPhaseDelayBetweenAttacks);
        this._timerDelayBetweenAttacks.onComplete = () => {
            if (this._timerDelayBetweenAttacks) {
                this._timerDelayBetweenAttacks.destroy();
                this._timerDelayBetweenAttacks = null;
            }

            this._beginNextAttack();
        };
    }

    /**
     * @private
     * @param {BubbleColor} matchColor
     * @param {Number} amount
     */
    _onBubblesCleared(matchColor, amount) {
        this._bubblesPopped[matchColor] += amount;
    }

    /**
     * @public
     */
    applyDamageToEnemies() {
        let damageToEnemies = [0, 0, 0];
        for (let i = 0; i < this._currentEnemies.length; ++i) {
            const enemy = this._currentEnemies[i];

            if (enemy) {
                for (let j = 0; j < this._bubblesPopped.length; ++j) {
                    if (j < BubbleColor.eSPECIAL || j === BubbleColor.eSTONE) {
                        let damage =
                            this._bubblesPopped[j] *
                            Common.settings.baseDamage *
                            this._waspDamageMultiplier *
                            this._allOutDamageMultiplier *
                            this._cheatDamageMultiplier;

                        damageToEnemies[i] += damage;

                        // If the enemy is weak to the colour, apply the damage a second time with a multiplier.
                        for (let k = 0; k < enemy.weakness.length; ++k) {
                            if (j === BUBBLE_COLOR_NAME_TO_ID[enemy.weakness[k]]) {
                                damageToEnemies[i] += damage * (Common.settings.weaknessDamageMultiplier - 1);
                            }
                        }
                    } else if (j === BubbleColor.eSPECIAL) {
                        damageToEnemies[i] +=
                            this._bubblesPopped[j] *
                            Common.settings.baseDamage *
                            this._waspDamageMultiplier *
                            this._allOutDamageMultiplier *
                            this._cheatDamageMultiplier;
                    }
                }
            }
        }

        for (let i = 0; i < this._currentEnemies.length; ++i) {
            const enemy = this._currentEnemies[i];

            if (enemy && damageToEnemies[i] > 0) {
                if (enemy.isShielded) {
                    damageToEnemies[i] = Math.round(damageToEnemies[i] * enemy.shieldDamageMultiplier);
                }
                Game.fire(GameEvents.eGAME_DAMAGE_ENEMY, i, -damageToEnemies[i], enemy);
                enemy.attacked(damageToEnemies[i]);

                if (!enemy.isShielded) {
                    Game.fire(GameEvents.eGAME_REMOVE_ENEMY_SHIELD, i);
                }
            }
        }

        this._bubblesPopped = [0, 0, 0, 0, 0, 0, 0, 0];
    }

    /**
     * Heal an enemy for hp
     * @private
     * @param {Number} id
     * @param {Number} health
     */
    _healEnemy(id, health) {
        this._currentEnemies[id].heal(health);
        Game.fire(GameEvents.eGAME_HEAL_ENEMY, id, health);
    }

    /**
     * An enemy is dead
     * @private
     */
    _onEnemyDead() {
        let allEnemiesDead = true;
        this._nbEnemies = 0;
        for (let i = 0; i < this._currentEnemies.length; ++i) {
            let enemy = this._currentEnemies[i];

            if (enemy && enemy.hp > 0) {
                ++this._nbEnemies;
                allEnemiesDead = false;
            } else if (enemy && enemy.hp <= 0) {
                enemy.destroy();
                this._currentEnemies[i] = null;
            }
        }

        if (!allEnemiesDead) {
            this._recalculateEnemyWeaknesses();
        }
    }

    /**
     * @private
     * @param {Object} response
     */
    _onRequestActiveEnemies(response) {
        const activeEnemies = [];
        for (const enemy of this._currentEnemies) {
            if (enemy && enemy.isAlive) {
                activeEnemies.push(enemy);
            }
        }

        response.activeEnemies = activeEnemies;
    }

    /**
     * Add the specified enemy in an empty enemy slot
     * @private
     * @param {Enemies} enemyName
     * @param {Function} callback
     */
    _onAbilityAddEnemy(enemyName, callback) {
        // The index of the first empty enemy
        let index = this._currentEnemies.indexOf(null);
        if (index !== -1) {
            if (this._nbEnemies < NB_MAX_ENEMIES) {
                ++this._nbEnemies;
            }
            Game.fire(GameEvents.eGAME_SET_NUMBER_OF_ENEMIES, this._nbEnemies, true);
        } else {
            // The index of the first dead enemy if no empty
            for (const enemy of this._currentEnemies) {
                if (!enemy.isAlive) {
                    index = enemy.index;
                    break;
                }
            }
            this._currentEnemies[index].destroy();
            this._currentEnemies[index] = null;
        }

        let enemy = this._createEnemy(enemyName, index, true);

        if (enemy !== null) {
            if (this._cheatEnemyAlwaysAttack) {
                enemy.cheatsEnemyAlwaysAttack(true);
            }
        }

        this._currentEnemies[index] = enemy;

        Game.audio.playSfx("IDSND_ENEMY_APPEARS");
        Game.fire(GameEvents.eGAME_SET_ENEMY, index, this._currentEnemies[index], this._currentEnemies[index].weakness, false);
        Game.fire(GameEvents.eGAME_SET_ENEMY_ATTACK_TIMER, index, this._currentEnemies[index].currentAttackTimer, false);

        this._recalculateEnemyWeaknesses();

        if (callback) {
            callback();
        }
    }

    /**
     * Ultron's Upgrade Ability : Heal
     * @private
     * @param {Enemy} ultron
     * @param {Number} heal
     * @param {Function} callback
     */
    _onUltronUpgradeHeal(ultron, heal, callback) {
        this._healEnemy(ultron.index, heal);

        if (callback) {
            callback();
        }
    }

    /**
     * Ultron's Upgrade Ability : Change random weaknesses
     * @private
     * @param {Enemy} ultron
     * @param {Function} callback
     */
    _onUltronUpgradeWeaknesses(ultron, callback) {
        ultron.setNewVariableWeaknesses(ultron.weakness);
        Common.hudEnemies.changeWeaknesses(ultron.index, ultron.weakness, callback);

        this._recalculateEnemyWeaknesses();
    }

    /**
     * Thanos' Reality Shift ability.  Changes random weaknesses.
     * @private
     * @param {Enemy} thanos
     * @param {Function} callback
     */
    _onThanosRealityShift(thanos, callback) {
        thanos.setNewVariableWeaknesses(thanos.weakness);
        Common.hudEnemies.changeWeaknesses(thanos.index, thanos.weakness, callback);

        this._recalculateEnemyWeaknesses();
    }

    /**
     * @private
     * @param {Number} heal
     * @param {Enemies} enemyName
     * @param {Function} callback
     */
    _onEnemyRegeneration(heal, enemyName, callback) {
        for (let i = 0; i < this._currentEnemies.length; ++i) {
            if (this._currentEnemies[i] && this._currentEnemies[i].name === enemyName) {
                this._healEnemy(i, heal);
                break;
            }
        }

        if (callback) {
            callback();
        }
    }

    /**
     * @private
     * @param {Number} healPercent
     * @param {Function} callback
     */
    _onEnemyRepair(healPercent, callback) {
        let selectedEnemy = 0;
        let hpRatio = 1;

        for (let i = 0; i < this._currentEnemies.length; ++i) {
            if (this._currentEnemies[i] && this._currentEnemies[i].isAlive && this._currentEnemies[i].isABoss) {
                selectedEnemy = i;
                break;
            }

            if (this._currentEnemies[i] && this._currentEnemies[i].isAlive) {
                let currentHPRatio = this._currentEnemies[i].hp / this._currentEnemies[i].maxHp;

                if (currentHPRatio <= hpRatio) {
                    hpRatio = currentHPRatio;
                    selectedEnemy = i;
                }
            }
        }

        let heal = Math.round((this._currentEnemies[selectedEnemy].maxHp * healPercent) / 100);
        this._currentEnemies[selectedEnemy].heal(heal);
        Game.fire(GameEvents.eGAME_HEAL_ENEMY, selectedEnemy, heal);

        if (callback) {
            callback();
        }
    }

    /**
     * @private
     * @param {Number} nbEnemies
     * @param {Number} nbTurns
     * @param {Number} shieldPercent
     * @param {Function} callback
     */
    _onEnemyShield(nbEnemies, nbTurns, shieldPercent, callback) {
        Game.audio.playSfx("IDSND_BOOST_DEFENSE_SHEILD");
        let randomEnemiesIndex = [];

        for (let i = 0; i < this._currentEnemies.length; ++i) {
            if (this._currentEnemies[i] && this._currentEnemies[i].isAlive) {
                randomEnemiesIndex.push(this._currentEnemies[i].index);
            }
        }

        randomEnemiesIndex = shuffle(randomEnemiesIndex);
        let selectedEnemies = [];
        let enemiesAdded = 0;

        for (let i = 0; i < randomEnemiesIndex.length; ++i) {
            selectedEnemies.push(randomEnemiesIndex[i]);
            this._currentEnemies[randomEnemiesIndex[i]].setShield(nbTurns, shieldPercent);
            ++enemiesAdded;

            if (enemiesAdded >= nbEnemies) {
                break;
            }
        }

        Game.fire(GameEvents.eGAME_SHIELD_ENEMIES, selectedEnemies, nbTurns);

        if (callback) {
            callback();
        }
    }

    /**
     * @private
     * @param {String} enemyName
     * @param {Number} index
     * @param {Boolean} [summoned] - Summoned by an ability
     * @returns {Enemy}
     */
    _createEnemy(enemyName, index, summoned = false) {
        let enemy = null;

        let enemySettingsFile = Game.design.getData("Enemies", DesignDataTypes.eGLOBAL_ENTITY);
        let enemyData = Game.design.findEntityByName(enemySettingsFile, enemyName);

        if (enemyData) {
            enemyData.damagedTimer = Common.settings.enemyShakeDuration;
            enemyData.multipleActionDelay = Common.settings.multipleActionDelay;
            enemyData.weaknessColors = this._availableColor;
        }

        switch (enemyName) {
            case Enemies.eRED_ROOM_GUARD_A:
                enemy = new RedRoomGuardA(enemyName, enemyData, index, summoned);
                break;
            case Enemies.eRED_ROOM_GUARD_B:
                enemy = new RedRoomGuardB(enemyName, enemyData, index, summoned);
                break;
            case Enemies.eRED_ROOM_GUARD_C:
                enemy = new RedRoomGuardC(enemyName, enemyData, index, summoned);
                break;
            case Enemies.eLOKI_WEAKENED:
                enemy = new LokiWeakened(enemyName, enemyData, index, summoned);
                break;
            case Enemies.eCHITAURI_ARTIFICIER:
                enemy = new ChitauriArtificier(enemyName, enemyData, index, summoned);
                break;
            case Enemies.eCHITAURI_FIGHTER:
                enemy = new ChitauriFighter(enemyName, enemyData, index, summoned);
                break;
            case Enemies.eCHITAURI_RIDER:
                enemy = new ChitauriRider(enemyName, enemyData, index, summoned);
                break;
            case Enemies.eLOKI:
                enemy = new Loki(enemyName, enemyData, index, summoned);
                break;
            case Enemies.eLOKI_COPY:
                enemy = new LokiCopy(enemyName, enemyData, index, summoned);
                break;
            case Enemies.eULTRONS_SENTRIES:
                enemy = new UltronsSentries(enemyName, enemyData, index, summoned);
                break;
            case Enemies.eHEAVY_ULTRONS_SENTRIES:
                enemy = new HeavyUltronsSentries(enemyName, enemyData, index, summoned);
                break;
            case Enemies.eSUPPORT_ULTRONS_SENTRIES:
                enemy = new SupportUltronsSentries(enemyName, enemyData, index, summoned);
                break;
            case Enemies.eLEVIATHAN:
                enemy = new Leviathan(enemyName, enemyData, index, summoned);
                break;
            case Enemies.eULTRON:
                enemy = new Ultron(enemyName, enemyData, index, summoned);
                break;
            case Enemies.eEBONY_MAW:
                enemy = new EbonyMaw(enemyName, enemyData, index, summoned);
                break;
            case Enemies.ePROXIMA_MIDNIGHT:
                enemy = new ProximaMidnight(enemyName, enemyData, index, summoned);
                break;
            case Enemies.eCORVUS_GLAIVE:
                enemy = new CorvusGlaive(enemyName, enemyData, index, summoned);
                break;
            case Enemies.eCULL_OBSIDIAN:
                enemy = new CullObsidian(enemyName, enemyData, index, summoned);
                break;
            case Enemies.eTHANOS:
                enemy = new Thanos(enemyName, enemyData, index, summoned);
                break;
            default:
                // Will return null if there's no enemy in this slot
                break;
        }
        return enemy;
    }

    /**
     * @private
     */
    _recalculateEnemyWeaknesses() {
        this._currentEnemiesWeaknesses = [];

        for (let i = 0; i < this._currentEnemies.length; ++i) {
            if (this._currentEnemies[i] && this._currentEnemies[i].isAlive) {
                for (let j = 0; j < this._currentEnemies[i].weakness.length; ++j) {
                    this._currentEnemiesWeaknesses[this._currentEnemies[i].weakness[j]] = true;
                }
            }
        }
    }

    /**
     * @private
     * @param {Boolean} enabled
     */
    _onCheatsEnemyAlwaysAttack(enabled) {
        this._cheatEnemyAlwaysAttack = enabled;

        if (this._cheatEnemyAlwaysAttack) {
            for (let i = 0; i < this._currentEnemies.length; ++i) {
                const enemy = this._currentEnemies[i];

                if (enemy && enemy.currentAttackTimer > 0) {
                    enemy.cheatsEnemyAlwaysAttack(enabled);

                    if (enabled) {
                        enemy.resetAttackTimer();
                        Game.fire(GameEvents.eGAME_SET_ENEMY_ATTACK_TIMER, i, enemy.currentAttackTimer, false);
                    }
                }
            }
        }
    }

    /**
     * @private
     * @param {Number} damageMultiplier
     */
    _onCheatsDamageMultiplier(damageMultiplier) {
        this._cheatDamageMultiplier = Game.utils.assertNumber(damageMultiplier, 1);
    }

    /**
     * Cheat that makes the current enemies 1 hp
     * @private
     */
    _onCheatDepleteEnemies() {
        let damageToEnemies = [0, 0, 0];
        for (let i = 0; i < this._currentEnemies.length; ++i) {
            const enemy = this._currentEnemies[i];

            if (!enemy) {
                continue;
            }

            damageToEnemies[i] = enemy.hp - 1;
        }

        for (let i = 0; i < this._currentEnemies.length; ++i) {
            const enemy = this._currentEnemies[i];

            if (enemy && damageToEnemies[i] > 0) {
                if (enemy.isShielded) {
                    damageToEnemies[i] = Math.round(damageToEnemies[i] * enemy.shieldDamageMultiplier);
                }
                Game.fire(GameEvents.eGAME_DAMAGE_ENEMY, i, -damageToEnemies[i], enemy);
                enemy.attacked(damageToEnemies[i]);

                if (!enemy.isShielded) {
                    Game.fire(GameEvents.eGAME_REMOVE_ENEMY_SHIELD, i);
                }
            }
        }
    }

    /**
     * @private
     * @param {Number} duration
     * @param {Number} damageMultiplier
     */
    _waspAbilityDamageMultiplier(duration, damageMultiplier) {
        this._waspDamageDuration = duration;
        this._waspDamageMultiplier = damageMultiplier;
    }

    /**
     * @private
     */
    _tickDownAbilityDurations() {
        if (this._waspDamageDuration > 0) {
            --this._waspDamageDuration;
            Game.fire(GameEvents.eGAME_SET_BUFF_TIMER, BuffDebuff.eSIZE_MANIPULATION, this._waspDamageDuration);

            if (this._waspDamageDuration === 0) this._waspDamageMultiplier = 1;
        }
        if (this._allOutDamageDuration > 0) {
            --this._allOutDamageDuration;

            if (this._allOutDamageDuration === 0) {
                this._allOutDamageMultiplier = 1;
                Game.fire(GameEvents.eHEROES_ALL_OUT_ATTACK_BUFF_COMPLETED);
            }
            Game.fire(GameEvents.eGAME_SET_BUFF_TIMER, BuffDebuff.eALL_OUT, this._allOutDamageDuration);
        }

        if (this._allOutDamageDuration <= 0 && this._waspDamageDuration <= 0) {
            Game.fire(GameEvents.eGAME_STOP_POWER_UP_BUFF_ANIMATION);
        }
    }

    /**
     * @private
     */
    _allOutAttackDamageMultiplier() {
        this._allOutDamageDuration = Common.settings.allOutDamageDuration + 1;
        this._allOutDamageMultiplier = Common.settings.allOutDamageMultiplier;
    }

    /**
     * Returns the enemies' collective weaknesses
     * @public
     * @returns {Boolean[]}
     */
    get currentEnemiesWeaknesses() {
        return this._currentEnemiesWeaknesses;
    }

    /**
     * Returns the current enemies
     * @public
     * @returns {Enemy[]}
     */
    get currentEnemies() {
        return this._currentEnemies;
    }

    /**
     * Is the all-out attack buff active?
     * @public
     * @returns {Boolean}
     */
    get isAllOutActive() {
        return this._allOutDamageMultiplier > 1;
    }

    /**
     * @public
     */
    closeLevel() {
        for (let enemy of this._currentEnemies) {
            if (enemy) {
                enemy.destroy();
                enemy = null;
            }
        }
        this._currentEnemies = [null, null, null];

        // Reset heroes abilities
        this._waspDamageDuration = 0;
        this._waspDamageMultiplier = 1;
        this._allOutDamageDuration = 0;
        this._allOutDamageMultiplier = 1;
    }
}
